import React, { useState, useEffect, useContext } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mapping des catégories avec icônes et couleurs
const CATEGORY_CONFIG = {
  auth: { label: "Authentification", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  mission: { label: "Missions", color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/30" },
  candidature: { label: "Candidatures", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" },
  contrat: { label: "Contrats", color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/30" },
  message: { label: "Messages", color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  retrocession: { label: "Retrocession", color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  admin: { label: "Administration", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30" },
  chat: { label: "Chat IA", color: "text-indigo-400", bg: "bg-indigo-500/10", border: "border-indigo-500/30" },
  profile: { label: "Profil", color: "text-teal-400", bg: "bg-teal-500/10", border: "border-teal-500/30" },
  system: { label: "Systeme", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30" },
};

const ACTION_LABELS = {
  register: "Inscription",
  login: "Connexion",
  account_locked: "Compte verrouille",
  email_verified: "Email verifie",
  password_reset: "Mot de passe reinitialise",
  google_login: "Connexion Google",
  google_register: "Inscription Google",
  create_mission: "Mission creee",
  apply_mission: "Candidature envoyee",
  accept_application: "Candidature acceptee",
  reject_application: "Candidature refusee",
  send_message: "Message envoye",
  create_contract: "Contrat cree",
  sign_contract: "Contrat signe",
  create_store_profile: "Profil magasin cree",
  create_optician_profile: "Profil opticien cree",
  update_store_profile: "Profil magasin mis a jour",
  create_retrocession_product: "Produit retrocession cree",
  create_retrocession_transaction: "Transaction retrocession",
  confirm_retrocession_transaction: "Transaction confirmee",
  admin_verify_optician: "Verification opticien",
  admin_update_user_status: "Statut utilisateur modifie",
  admin_delete_user: "Utilisateur supprime",
  chat_ai_expert: "Chat IA expert",
};

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function ActivityLogs({ token }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const LIMIT = 30;

  useEffect(() => {
    fetchLogs();
  }, [filter, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/logs/my-activity?skip=${page * LIMIT}&limit=${LIMIT}`;
      if (filter !== "all") {
        url += `&category=${filter}`;
      }
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Erreur chargement logs:", err);
    }
    setLoading(false);
  };

  const categories = ["all", ...Object.keys(CATEGORY_CONFIG)];

  return (
    <div data-testid="activity-logs-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="activity-logs-title">
            Journal d'activite
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            {total} actions enregistrees
          </p>
        </div>
        <button
          data-testid="refresh-logs-btn"
          onClick={fetchLogs}
          className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg transition-all"
        >
          Actualiser
        </button>
      </div>

      {/* Filtres par categorie */}
      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        {categories.map((cat) => {
          const conf = cat === "all" ? null : CATEGORY_CONFIG[cat];
          return (
            <button
              key={cat}
              data-testid={`filter-${cat}`}
              onClick={() => {
                setFilter(cat);
                setPage(0);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filter === cat
                  ? "bg-blue-600/40 text-blue-200 border border-blue-400/50"
                  : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10"
              }`}
            >
              {cat === "all" ? "Tout" : conf?.label || cat}
            </button>
          );
        })}
      </div>

      {/* Liste des logs */}
      <div className="space-y-2" data-testid="logs-list">
        {loading ? (
          <div className="text-center py-12 text-slate-400">
            Chargement des logs...
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-slate-400" data-testid="no-logs">
            Aucune activite trouvee
          </div>
        ) : (
          logs.map((log, idx) => {
            const conf = CATEGORY_CONFIG[log.category] || CATEGORY_CONFIG.system;
            return (
              <div
                key={log.id || idx}
                data-testid={`log-entry-${idx}`}
                className={`flex items-start gap-4 p-4 rounded-xl border ${conf.bg} ${conf.border} transition-all hover:scale-[1.01]`}
              >
                {/* Badge categorie */}
                <div className={`px-2.5 py-1 rounded-md text-xs font-semibold ${conf.bg} ${conf.color} border ${conf.border} whitespace-nowrap`}>
                  {conf.label}
                </div>

                {/* Contenu */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-white text-sm">
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                    {log.status === "failure" && (
                      <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-medium border border-red-500/30">
                        Echec
                      </span>
                    )}
                  </div>
                  <p className="text-slate-300 text-sm mt-1 truncate">
                    {log.description}
                  </p>
                  {log.ip_address && (
                    <p className="text-slate-500 text-xs mt-1">
                      IP: {log.ip_address}
                    </p>
                  )}
                </div>

                {/* Date */}
                <div className="text-slate-500 text-xs whitespace-nowrap">
                  {formatDate(log.created_at)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="flex items-center justify-center gap-4 pt-4" data-testid="pagination">
          <button
            data-testid="prev-page-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-4 py-2 bg-white/5 text-slate-400 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Precedent
          </button>
          <span className="text-slate-400 text-sm">
            Page {page + 1} / {Math.ceil(total / LIMIT)}
          </span>
          <button
            data-testid="next-page-btn"
            disabled={(page + 1) * LIMIT >= total}
            onClick={() => setPage((p) => p + 1)}
            className="px-4 py-2 bg-white/5 text-slate-400 rounded-lg hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}
